import { Avatar, Form } from 'antd';
import anybody_icon from 'assets/Icons/icon-anybody.png';
import clsx from 'clsx';
import CalendarPicker from 'components/CalendarPicker';
import { ANY_BODY_ID_STORE_LIST } from 'features/booking/services/constants';
import moment, { Moment } from 'moment';
import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { IStaffItemData } from 'services/shop/types/staff';
import styled from 'styled-components';
import useGetExitsAppointment from '../services/hooks/useGetExitsAppointment';
import img_staff_active from './assets/staff-active.svg';
import ContinueBookingModal, { ContinueBookingModalRef } from './ContinueBookingModal';
import useInquiryDayOffs from './useInquiryDayOffs';
import useQueryAvailableBooking from './useQueryAvailableBooking';
import useInquiryStaffList from './useInquiryStaffList';
import { IFormValueServiceItem, IGroupCustomerItem } from './types';
type DateStaffStepRef = {
  fetch: (date: string, customerId?: string) => void;
};
export const dateStaffStepRef = React.createRef<DateStaffStepRef>();
export const continueBookingModalRef = React.createRef<ContinueBookingModalRef>();
interface DateStaffStepProps {
  v4?: boolean;
}

const DateStaffStep = forwardRef<DateStaffStepRef, DateStaffStepProps>(({ v4: bookingOnlineV4 }: DateStaffStepProps, ref) => {
  const form = Form.useFormInstance();
  const dayOffs: Moment[] = Form.useWatch('dayOffs', form) || [];
  const [fetchExitsAppointment] = useGetExitsAppointment((data) => {
    continueBookingModalRef.current?.open(data);
  });
  const cacheDate = useRef({
    startTime: '',
    endTime: '',
  });

  const [, fetch] = useQueryAvailableBooking({
    onStart() {
      form.resetFields(['startTime', 'TIME_SLOT_VALID', 'SERVICE_VALID', 'DATE_STAFF_VALID', 'staff']);
    },
    onDone(data) {
      form.setFieldValue('staffList', data);
    },
  });

  const [, fetchWithBookingV4] = useInquiryStaffList({
    onStart() {
      form.resetFields(['staff', 'startTime', 'TIME_SLOT_VALID', 'DATE_STAFF_VALID']);
    },
    onDone(data) {
      form.setFieldValue('staffList', data);
    },
  });

  const [_dateOffState, fetchDateOff] = useInquiryDayOffs({
    onStart: ({ endTime, startTime }) => {
      cacheDate.current.startTime = startTime;
      cacheDate.current.endTime = endTime;
    },
    onDone(data) {
      form.setFieldValue('dayOffs', data);
    },
  });

  const handleFetch = (date: string, _customerId?: string) => {
    const customerId = _customerId || form.getFieldValue('customerId') || '';
    if(bookingOnlineV4) {
        const customer: IGroupCustomerItem = form.getFieldValue('customers')[0] || {};
        const serviceIds = (customer.services || []).map((service: IFormValueServiceItem) => service.id) || [];
        fetchWithBookingV4(serviceIds, undefined, undefined);
    } else {
      fetch(date, customerId);
      if (customerId) fetchExitsAppointment(customerId, date);
    }
    const now = moment(date, 'MM-DD-YYYY');
    const startTime = now.clone().subtract(1, 'month').startOf('month').format('MM-DD-YYYY');
    const endTime = now.add(1, 'month').endOf('month').format('MM-DD-YYYY');
    if (cacheDate.current.startTime === startTime && cacheDate.current.endTime === endTime) return;
    fetchDateOff(startTime, endTime);
  };

  const onMonthChange = (now: Moment) => {
    const startTime = now.clone().subtract(1, 'month').startOf('month').format('MM-DD-YYYY');
    const endTime = now.clone().add(1, 'month').endOf('month').format('MM-DD-YYYY');
    fetchDateOff(startTime, endTime);
    form.setFieldValue('date', now);
    const customerId = form.getFieldValue('customerId') || '';
    if(bookingOnlineV4) {
      const customer: IGroupCustomerItem = form.getFieldValue('customers')[0] || {};
      const serviceIds = (customer.services || []).map((service: IFormValueServiceItem) => service.id) || [];
      fetchWithBookingV4(serviceIds, undefined, undefined);
    } else {
      fetch(now.format('MM-DD-YYYY'), customerId);
    }
  };

  useImperativeHandle(ref, () => ({
    fetch: handleFetch,
  }));

  return (
    <>
      <Container>
        <Form.Item noStyle name={'date'} rules={[{ required: true, message: 'please choose date' }]}>
          <CalendarPicker
            offDates={dayOffs || _dateOffState.data}
            onChange={(val) => handleFetch(val.format('MM-DD-YYYY'))}
            onMonthChange={onMonthChange}
          />
        </Form.Item>
        <div style={{ marginBottom: 16 }} />
        <Form.Item name={'staff'} noStyle rules={[{ required: true, message: 'Please select the staff' }]}>
          <StaffList />
        </Form.Item>
        <ContinueBookingModal ref={continueBookingModalRef} />
      </Container>
    </>
  );
});
DateStaffStep.displayName = 'DateStaffStep';
export default DateStaffStep;

const Container = styled.div`
  .CalendarAntUI {
    margin: 0 -8px;
    .ant-picker-cell .box {
      color: #1d2129;
      height: 30px;
      width: 30px;
      text-align: center;
      font-family: Poppins;
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: normal;
    }

    .ant-picker-cell.ant-picker-cell-selected .box {
      color: #fff;
      background: #2463eb;
    }
    .ant-picker-content tbody td {
      height: 20px;
      padding: 0;
    }

    .ant-picker-content thead th {
      color: #505050;
      text-align: center;
      font-family: Poppins;
      font-size: 12px;
      font-style: normal;
      font-weight: 600;
      line-height: 30px; /* 214.286% */
      text-transform: capitalize;
    }
  }
`;

type StaffListProps = {
  value?: IStaffItemData;
  onChange?: (value?: IStaffItemData) => void;
};
const StaffList = ({ value, onChange }: StaffListProps) => {
  const form = Form.useFormInstance();
  const staffList: IStaffItemData[] = Form.useWatch('staffList', form) || [];

  return (
    <ContainerList>
      {staffList.map((staff) => (
        <button
          key={staff.id}
          type='button'
          className={clsx('staff-item', value?.id === staff.id && 'active')}
          onClick={() => {
            onChange?.(staff);
            form.resetFields(['DATE_STAFF_VALID', 'TIME_SLOT_VALID']);
            setTimeout(() => {
              form.submit();
            }, 100);
          }}
        >
          <Avatar src={ANY_BODY_ID_STORE_LIST.includes(staff.id) ? anybody_icon : (staff.urlImage ? staff.urlImage : undefined)} size={55}>
            {staff.nickName?.[0] || 'A'}
          </Avatar>
          <span className='staff-name'>{staff.nickName}</span>
          {value?.id === staff.id && (
            <div className='staff-active-icon'>
              <img src={img_staff_active} alt='img_staff_active' style={{ width: 28, height: 28 }} />
            </div>
          )}
        </button>
      ))}
    </ContainerList>
  );
};

const ContainerList = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 8px;
  .staff-item {
    display: flex;
    padding: 8px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 4px;
    flex: 1 0 0;
    border-radius: 7.347px;
    border: 1px solid #c9cdd4;
    background: #fff;
    position: relative;
    box-shadow: -0.73465px 0.73465px 2.93861px 0px rgba(0, 0, 0, 0.1) inset,
      0px 2.93861px 2.93861px 0px rgba(0, 0, 0, 0.25);

    .staff-name {
      color: #1d2129;
      text-align: center;
      font-family: Blinker;
      font-size: 14px;
      font-style: normal;
      font-weight: 600;
      line-height: 137.5%; /* 19.25px */
      letter-spacing: 0.28px;

      overflow: hidden;
      text-overflow: ellipsis;
      white-space: initial;
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
    }

    .staff-active-icon {
      position: absolute;
      right: 4px;
      top: 4px;
    }

    &.active {
      z-index: 99;
      border-radius: 7.347px;
      border: 1px solid #86909c;
      background: #d7edff;
    }
  }
`;
